const getCsrfToken = (): string | null => {
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "csrftoken") {
      return value;
    }
  }
  return null;
};

export function toCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  } else if (obj && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, char) =>
        char.toUpperCase()
      );
      acc[camelKey] = toCamelCase(obj[key]);
      return acc;
    }, {} as any);
  }
  return obj;
}

const refreshAccessToken = async (): Promise<boolean> => {
  const refresh = localStorage.getItem("refreshToken");
  if (!refresh) return false;

  let baseUrl = process.env.REACT_APP_SERVER_URL || "";
  if (baseUrl.endsWith("/")) {
    baseUrl = baseUrl.slice(0, -1);
  }

  try {
    const res = await fetch(`${baseUrl}/auth/refresh/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh }),
    });

    if (!res.ok) throw new Error("Refresh failed");

    const data = await res.json();
    localStorage.setItem("accessToken", data.access);
    return true;
  } catch (err) {
    console.error("Token refresh failed:", err);
    return false;
  }
};

const fetchWithCredentials = async (
  url: string,
  options: RequestInit = {}
): Promise<any> => {
  const csrfToken = getCsrfToken();
  let token = localStorage.getItem("accessToken");

  const headers = {
    ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
    ...(!url.endsWith("/upload") ? { "Content-Type": "application/json" } : {}),
    ...options.headers,
    Authorization: `Bearer ${token}`,
  };

  let baseUrl = process.env.REACT_APP_SERVER_URL || "";
  if (baseUrl.endsWith("/")) {
    baseUrl = baseUrl.slice(0, -1);
  }

  const makeRequest = async (): Promise<Response> => {
    return fetch(`${baseUrl}${url}`, {
      credentials: "include",
      ...options,
      headers,
    });
  };

  let response = await makeRequest();

  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Try again with new access token
      token = localStorage.getItem("accessToken")!;
      headers.Authorization = `Bearer ${token}`;
      response = await makeRequest();
    } else {
      // Logout if refresh failed
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("teacherName");
      window.location.href = "/login";
      return;
    }
  }

  if (!response.ok) {
    let errorMessage = "An unexpected error occurred";

    try {
      const errorData: any = await response.json();
      if (
        typeof errorData === "object" &&
        errorData !== null &&
        "error" in errorData
      ) {
        errorMessage = errorData.error;
      }
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // Silent fail — keep default error message
      }
    }

    console.error("Error response:", errorMessage);
    // Include status code in error message for better error handling
    throw new Error(`${response.status}: ${errorMessage}`);
  }

  const contentType = response.headers.get("Content-Type");
  if (contentType?.includes("application/json")) {
    const json = await response.json();
    return toCamelCase(json);
  }

  return null;
};

export const uploadFile = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  const data = await fetchWithCredentials("/api/upload", {
    method: "POST",
    body: formData,
  });
  return data.url;
};

export const connectToWebSocket = (
  url: string,
  onMessage: (event: MessageEvent) => void,
  onError?: (error: Event) => void,
  onClose?: (event: CloseEvent) => void
) => {
  let socket: WebSocket | null = null;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const reconnectDelay = 1000; // Start with 1 second

  const connect = async (): Promise<WebSocket> => {
    // Always try to refresh token before connecting to ensure it's fresh
    let token = localStorage.getItem("accessToken");
    let refreshed = false;

    if (token) {
      // Try to refresh the token proactively
      refreshed = await refreshAccessToken();
      if (refreshed) {
        token = localStorage.getItem("accessToken");
      }
    } else {
      // No token available, try to refresh
      refreshed = await refreshAccessToken();
      if (refreshed) {
        token = localStorage.getItem("accessToken");
      } else {
        throw new Error("No valid token available");
      }
    }

    const baseUrl = process.env.REACT_APP_SERVER_URL || "ws://localhost:8000";
    const wsBaseUrl = baseUrl.replace(/^http/, "ws");
    const fullUrl = `${wsBaseUrl}${url}?token=${token}`;

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(fullUrl);

      ws.onopen = () => {
        reconnectAttempts = 0; // Reset reconnect attempts on successful connection
        resolve(ws);
      };

      ws.onerror = (error) => {
        reject(error);
      };

      // Set a timeout for connection
      setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.close();
          reject(new Error("WebSocket connection timeout"));
        }
      }, 10000); // 10 second timeout
    });
  };

  const setupSocket = async () => {
    try {
      socket = await connect();

      socket.onmessage = onMessage;

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
        onError?.(error);
      };

      // Set up periodic token refresh to prevent expiration
      tokenRefreshInterval = setInterval(async () => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          await refreshAccessToken();
        }
      }, 5 * 60 * 1000); // Refresh every 5 minutes

      socket.onclose = async (event) => {
        if (tokenRefreshInterval) {
          clearInterval(tokenRefreshInterval);
          tokenRefreshInterval = null;
        }

        onClose?.(event);

        // Don't reconnect if it was a normal closure or if we've exceeded max attempts
        if (event.code === 1000 || reconnectAttempts >= maxReconnectAttempts) {
          return;
        }

        // Check if it's an authentication error or immediate closure (likely token issue)
        if (
          event.code === 4001 ||
          event.reason?.includes("token") ||
          event.reason?.includes("Authentication") ||
          event.code === 1006 || // Abnormal closure
          (event.code === 1000 && !event.wasClean) // Normal closure but not clean
        ) {
          // Try to refresh token
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("teacherName");
            window.location.href = "/login";
            return;
          }
          // If refresh was successful, attempt immediate reconnection
          setTimeout(() => {
            if (reconnectAttempts < maxReconnectAttempts) {
              setupSocket();
            }
          }, 100);
          return;
        }

        // Attempt to reconnect with exponential backoff
        reconnectAttempts++;
        const delay = reconnectDelay * Math.pow(2, reconnectAttempts - 1);

        setTimeout(() => {
          if (reconnectAttempts < maxReconnectAttempts) {
            setupSocket();
          }
        }, delay);
      };
    } catch (error) {
      // If it's a token-related error, try to refresh and reconnect
      if (error instanceof Error && error.message.includes("token")) {
        const refreshed = await refreshAccessToken();
        if (refreshed && reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          setTimeout(() => setupSocket(), reconnectDelay);
        }
      }
    }
  };

  // Start the connection
  setupSocket();

  // Return a socket-like object with a close method
  let tokenRefreshInterval: NodeJS.Timeout | null = null;

  const socketWrapper = {
    close: () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        tokenRefreshInterval = null;
      }
      if (socket) {
        socket.close(1000, "Manual close");
      }
    },
    send: (data: Parameters<WebSocket["send"]>[0]) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(data);
      }
    },
    readyState: WebSocket.CONNECTING as number,
  };

  // Update readyState when socket is available
  const updateReadyState = () => {
    if (socket) {
      socketWrapper.readyState = socket.readyState;
    }
  };

  // Set up an interval to update readyState
  const interval = setInterval(updateReadyState, 100);

  // Override close to also clear the interval
  const originalClose = socketWrapper.close;
  socketWrapper.close = () => {
    clearInterval(interval);
    originalClose();
  };

  return socketWrapper;
};

export default fetchWithCredentials;
